import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Form,
  Input,
  Message,
  RadioGroup,
  Radio,
} from 'rsuite';
import authFetch from '../../helpers/authFetch';
import { DECK_FORMAT_RULES } from '../../helpers/deckRules';
import { CollectionTypeSelector, DeckTypeSelector } from './CollectionTypeSelectors';
import { usePreferences } from '../Preferences/PreferencesContext';

const FALLBACK_VISIBILITY = 'PRIVATE';

const createInitialForm = (preferences, defaultValues = {}) => ({
  name: '',
  type: defaultValues.type || null,
  deck_type: defaultValues.deck_type ?? (defaultValues.type === 'DECK' ? 'OTHER' : null),
  description: '',
  visibility: preferences?.default_deck_visibility || FALLBACK_VISIBILITY,
});

const CreateCollectionModal = ({ open, onClose, onCreated, defaultValues }) => {
  const { preferences } = usePreferences();
  const [formData, setFormData] = useState(() => createInitialForm(preferences, defaultValues));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const prevOpenRef = useRef(false);

  // Reset form with current preferences only when modal opens (not while open)
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    // Only reset when transitioning from closed to open
    if (open && !wasOpen) {
      setFormData(createInitialForm(preferences, defaultValues));
      setError('');
    }
  }, [open, preferences, defaultValues]);

  // Generate deck format hints based on selected deck_type
  const deckHints = useMemo(() => {
    if (!formData.deck_type || formData.deck_type === 'OTHER') {
      return null;
    }

    const rules = DECK_FORMAT_RULES[formData.deck_type];
    if (!rules) {
      return null;
    }

    const hints = [];

    // Deck size hint
    if (rules.minDeckSize !== null && rules.maxDeckSize !== null) {
      hints.push(`Exactly ${rules.minDeckSize} cards`);
    } else if (rules.minDeckSize !== null) {
      hints.push(`Minimum ${rules.minDeckSize} cards`);
    }

    // Copy limit hint
    if (rules.singleton) {
      hints.push('Singleton: 1 copy per card');
    } else if (rules.maxCopies !== null) {
      hints.push(`Max ${rules.maxCopies} copies per card`);
    }

    // Sideboard hint
    if (rules.sideboardSize === 0) {
      hints.push('No sideboard');
    } else if (rules.sideboardSize !== null) {
      hints.push(`Up to ${rules.sideboardSize} card sideboard`);
    }

    // Basic land exemption
    if (rules.basicLandExempt && !rules.singleton) {
      hints.push('Basic lands exempt from copy limit');
    }

    // Commander-specific and Oathbreaker hints
    if (rules.requiresCommander) {
      if (formData.deck_type === 'OATHBREAKER') {
        hints.push(`Requires a ${rules.commanderLabel} (Planeswalker)`);
        hints.push('Requires a Signature Spell (Instant or Sorcery)');
        hints.push(`Cards must match ${rules.commanderLabel.toLowerCase()} color identity`);
      } else if (formData.deck_type === 'BRAWL' || formData.deck_type === 'STANDARD_BRAWL') {
        hints.push('Requires a Legendary Creature or Planeswalker as commander');
      } else {
        hints.push('Requires a Legendary Creature as commander');
        hints.push('Cards must match commander color identity');
      }
    }

    // Brawl-specific hints
    if (formData.deck_type === 'STANDARD_BRAWL') {
      hints.push('MTG Arena Standard-legal cards only');
    } else if (formData.deck_type === 'BRAWL') {
      hints.push('MTG Arena Historic-legal cards only');
    }

    // Planar Standard-specific hints
    if (formData.deck_type === 'PLANAR_STANDARD') {
      hints.push('Universe Within cards only');
      if (rules.legalSetNames) {
        const setNames = Object.values(rules.legalSetNames).join(', ');
        hints.push(`Legal sets: ${setNames}`);
      } else if (rules.legalSets && rules.legalSets.length > 0) {
        hints.push(`Legal sets: ${rules.legalSets.join(', ').toUpperCase()}`);
      }
    }

    return hints.length > 0 ? hints : null;
  }, [formData.deck_type]);

  const handleClose = () => {
    setFormData(createInitialForm(preferences));
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    setError('');

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.type) {
      setError('Type is required');
      return;
    }

    if (formData.type === 'DECK' && !formData.deck_type) {
      setError('Deck type is required');
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        name: formData.name.trim(),
        type: formData.type,
        description: formData.description.trim() || undefined,
        visibility: formData.visibility,
      };

      if (formData.type === 'DECK') {
        body.deck_type = formData.deck_type;
      }

      const response = await authFetch('/collections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to create');
        return;
      }

      const created = await response.json();
      handleClose();
      if (onCreated) onCreated(created);
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setSubmitting(false);
    }
  };

  const setField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'type') {
        if (value === 'DECK') {
          // Set deck_type from preference if available, otherwise default to OTHER
          next.deck_type = preferences.default_deck_format ?? 'OTHER';
        } else {
          next.deck_type = null;
        }
      }
      return next;
    });
  };

  return (
    <Modal open={open} onClose={handleClose} size="sm">
      <Modal.Header>
        <Modal.Title>Create New Deck, Binder, or Wish List</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Message type="error" showIcon style={{ marginBottom: 16 }}>
            {error}
          </Message>
        )}
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Name</Form.ControlLabel>
            <Input
              value={formData.name}
              onChange={(value) => setField('name', value)}
              placeholder="Collection name"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Type</Form.ControlLabel>
            <CollectionTypeSelector
              value={formData.type}
              onChange={(value) => setField('type', value)}
            />
          </Form.Group>

          {formData.type === 'DECK' && (
            <>
              <Form.Group>
                <Form.ControlLabel>Deck Type</Form.ControlLabel>
                <DeckTypeSelector
                  value={formData.deck_type}
                  onChange={(value) => setField('deck_type', value)}
                />
              </Form.Group>

              {deckHints && (
                <Message type="info" showIcon style={{ marginBottom: 16 }}>
                  <div>
                    {deckHints.map((hint, idx) => (
                      <div key={idx} style={{ marginBottom: idx < deckHints.length - 1 ? 6 : 0 }}>
                        • {hint}
                      </div>
                    ))}
                  </div>
                </Message>
              )}
            </>
          )}

          <Form.Group>
            <Form.ControlLabel>Description</Form.ControlLabel>
            <Input
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(value) => setField('description', value)}
              placeholder="Optional description"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Visibility</Form.ControlLabel>
            <RadioGroup
              value={formData.visibility}
              onChange={(value) => setField('visibility', value)}
            >
              <Radio value="PRIVATE">Private (Only you)</Radio>
              <Radio value="INVITE_ONLY">Invite Only (Share with friends)</Radio>
              <Radio value="PUBLIC">Public (Anyone with link)</Radio>
            </RadioGroup>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle">
          Cancel
        </Button>
        <Button onClick={handleSubmit} appearance="primary" loading={submitting}>
          Create
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateCollectionModal;
